import { Field } from '@campfire/field';
import { RichEditorFormik } from '@campfire/campfire-rich-editor';
import { Select } from '@campfire/select';
import { FileUploadGallery, FugFile } from '@campfire/file-upload-gallery';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select as MuiSelect,
  Typography,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { createStyles, makeStyles } from '@material-ui/styles';
import { useFormikContext } from 'formik';
import { capitalize } from 'lodash';
import React, { useState } from 'react';
import { AutoLocationField } from '../../../../../common/auto-location-field/AutoLocationField';
import { ActivityFormValues } from '../ActivityFormV2';
import { CreateActivityProgramV2 } from '../__generated__/CreateActivityProgramV2';
import { ActivityTag } from '../__generated__/ActivityTag';

interface ActivityDetailsStepProps {
  programs: CreateActivityProgramV2[];
  activityTags: ActivityTag[];
}

const useStyles = makeStyles(() =>
  createStyles({
    fieldLabel: {
      fontSize: 14,
      fontWeight: 700,
      color: '#1B1612',
      marginBottom: 4,
    },
    fieldDescription: {
      fontSize: 13,
      color: '#808080',
      marginBottom: 12,
    },
  })
);

const FieldHeader = ({ label, description }: { label: string; description: string }) => {
  const classes = useStyles();
  return (
    <>
      <Typography className={classes.fieldLabel}>{label}</Typography>
      <Typography className={classes.fieldDescription}>{description}</Typography>
    </>
  );
};

export const ActivityDetailsStep = ({ programs, activityTags }: ActivityDetailsStepProps) => {
  const { values, setFieldValue, errors } = useFormikContext<ActivityFormValues>();
  const [tagsSelectOpen, setTagsSelectOpen] = useState(false);

  return (
    <Box>
      <Typography style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Details</Typography>
      <Typography variant='body2' color='textSecondary' style={{ marginBottom: 24 }}>
        Pick a clear and recognisable name for your activity - this will be the first thing volunteers see.
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} sm={6}>
          <FieldHeader
            label='Name'
            description='Pick a clear and recognisable name for your activity - this will be the first thing volunteers see and should describe the opportunity.'
          />
          <Field name='activityName' placeholder='Activity Name' variant='outlined' helper fullWidth required />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FieldHeader
            label='Program'
            description="Select this activity's program. Only volunteers and managers of this program will be able to see, join and interact with it."
          />
          <Select
            slow
            fullWidth
            variant='outlined'
            name='programId'
            label='Program'
            items={programs}
            renderFunction={(program: CreateActivityProgramV2) => program.name}
            valueFunction={(program: CreateActivityProgramV2) => program.programId}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FieldHeader label='Description' description='Describe the activity to current and prospective volunteers.' />
          <RichEditorFormik valueName='activityDescription' placeholder='Describe your activity...' />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FieldHeader
            label='Location'
            description='Set the default location for this activity. Individual sessions can have their own addresses.'
          />
          <ToggleButtonGroup
            value={values.locationType}
            exclusive
            onChange={(_unusedEvent, newValue) => {
              if (!newValue) return;
              setFieldValue('locationType', newValue);
              if (newValue === 'remote') {
                setFieldValue('location', null);
                setFieldValue('remoteLocation', { details: '' });
              }
              if (newValue === 'local') {
                setFieldValue('remoteLocation', null);
              }
            }}
            style={{ marginBottom: 12 }}
          >
            <ToggleButton
              value='local'
              style={
                values.locationType === 'local'
                  ? { backgroundColor: '#7047EB', color: '#fff', padding: '6px 14px' }
                  : { padding: '6px 14px' }
              }
            >
              <Typography style={{ fontWeight: 600, fontSize: 11 }}>Local</Typography>
            </ToggleButton>
            <ToggleButton
              value='remote'
              style={
                values.locationType === 'remote'
                  ? { backgroundColor: '#7047EB', color: '#fff', padding: '6px 14px' }
                  : { padding: '6px 14px' }
              }
            >
              <Typography style={{ fontWeight: 600, fontSize: 11 }}>Remote</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
          {values.locationType === 'local' && (
            <Box mt={1}>
              <AutoLocationField name='location' label='Location' helper fullWidth variant='outlined' />
            </Box>
          )}
          {values.locationType === 'remote' && (
            <Box mt={1}>
              <Field
                name='remoteLocation.details'
                label='Add additional info (URL, phone number, etc.)'
                variant='outlined'
                helper
                fullWidth
              />
            </Box>
          )}
        </Grid>

        <Grid item xs={12} sm={3}>
          <FieldHeader label='Tags' description='Sort and categorise your activities.' />
          {activityTags.length > 0 ? (
            <FormControl fullWidth>
              <InputLabel
                htmlFor='select-activity-tags-wizard'
                style={{ left: '14px', top: '-4px', backgroundColor: '#FFFFFF', zIndex: 1 }}
              >
                Activity Tags
              </InputLabel>
              <MuiSelect
                fullWidth
                open={tagsSelectOpen}
                onOpen={() => setTagsSelectOpen(true)}
                onClose={() => setTagsSelectOpen(false)}
                multiple
                variant='filled'
                value={values.activityTagIds}
                onChange={(event) => {
                  const selectedTags = (event.target.value as string[]).filter((x) => !!x);
                  setFieldValue('activityTagIds', selectedTags);
                }}
                input={<OutlinedInput id='select-activity-tags-wizard' label='Chip' />}
                renderValue={(tagIds) => (
                  <Grid container spacing={1}>
                    {(tagIds as string[]).map((tagId) => {
                      const selectedTag = activityTags.find((x) => x.activityTagId === tagId);
                      return selectedTag ? (
                        <Grid item key={selectedTag.activityTagId}>
                          <Chip color='primary' label={capitalize(selectedTag.name)} />
                        </Grid>
                      ) : null;
                    })}
                  </Grid>
                )}
              >
                {activityTags
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((tag) => (
                    <MenuItem key={tag.activityTagId} value={tag.activityTagId}>
                      <Checkbox
                        color='primary'
                        checked={!!values.activityTagIds.find((tagId) => tagId === tag.activityTagId)}
                      />
                      {tag.name}
                    </MenuItem>
                  ))}
              </MuiSelect>
            </FormControl>
          ) : (
            <Typography variant='body2' color='textSecondary'>
              No tags created yet.
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={3}>
          <FieldHeader label='Attachments' description='Files visible to all volunteers.' />
          <FileUploadGallery
            id='wizard-addedAttachments'
            allowMultiple
            uploadedFiles={(values.addedAttachments as FugFile[]) ?? []}
            handleFilesAdded={(files: FugFile[]) => {
              const newFiles = values.addedAttachments.concat(files);
              setFieldValue('addedAttachments', newFiles);
              setFieldValue(
                'creationTokens',
                newFiles.map((file) => file.id)
              );
            }}
            handleFileRemoved={(removedFile: FugFile) => {
              setFieldValue(
                'addedAttachments',
                values.addedAttachments.filter((file) => file.id !== removedFile.id)
              );
              setFieldValue(
                'creationTokens',
                values.creationTokens.filter((token) => token !== removedFile.id)
              );
              setFieldValue('removedAttachmentIds', [...values.removedAttachmentIds, removedFile.id]);
            }}
          />
          <Typography variant='subtitle2' color='error'>
            {errors.addedAttachments}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};
